import React from "react";
import "./Home.css";
import { Link } from "react-router-dom";
import {
  companyInfo,
  companyStats,
  featuredProjects,
  galleryItems,
  promisePoints,
  serviceGroups,
} from "../../data/siteData";

const servicePreviews = [
  {
    title: "Gates & exterior metalwork",
    detail: "Gates, fencing and bespoke exterior features for homes and estates.",
    image: galleryItems[6].image,
    alt: "Decorative metal gate installation",
  },
  {
    title: "Decorative & laser-cut designs",
    detail: "Custom screens, ornamental pieces and striking architectural details.",
    image: galleryItems[12].image,
    alt: "Decorative metal screen with a laser-cut pattern",
  },
  {
    title: "Railings & stainless steel",
    detail: "Staircases, balustrades and considered interior metal finishes.",
    image: featuredProjects[2].image,
    alt: "Metal railing along an interior staircase",
  },
];

function Home() {
  const [mainProject, secondProject, thirdProject] = featuredProjects;

  return (
    <div className="page page--home">
      <section className="tm-hero" aria-labelledby="home-heading">
        <div className="tm-hero__copy">
          <p className="tm-eyebrow"><span className="tm-dot" /> Touchmark Limited · Nigeria</p>
          <h1 id="home-heading">
            Metalwork that makes a <em>lasting impression.</em>
          </h1>
          <p className="tm-hero__description">
            From architectural gates and wrought iron detailing to stainless steel
            finishes and structural fabrication, we bring form and function
            together in every project.
          </p>
          <div className="tm-hero__actions">
            <Link className="tm-action tm-action--primary" to="/projects">
              Explore our work <span aria-hidden="true">↗</span>
            </Link>
            <Link className="tm-action tm-action--secondary" to="/contact">
              Request a quotation <span aria-hidden="true">→</span>
            </Link>
          </div>
          <div className="tm-hero__footnote">
            <span><strong>Established {companyInfo.founded}</strong><small>Decades of industry experience</small></span>
            <span><strong>Abuja · Lagos · Enugu</strong><small>Residential and commercial work</small></span>
          </div>
        </div>
        <div className="tm-hero__visual">
          <img
            className="tm-hero__photo"
            src={galleryItems[6].image}
            alt="Decorative steel gate with patterned detailing"
            fetchPriority="high"
          />
          <div className="tm-hero__overlay">
            <span>01 / The work speaks</span>
            <strong>Built with purpose.<br />Finished with care.</strong>
          </div>
          <div className="tm-hero__image-accent" aria-hidden="true" />
        </div>
      </section>

      <section className="tm-trust" aria-label="Touchmark at a glance">
        {companyStats.slice(0, 3).map((stat, index) => (
          <div className="tm-trust__item" key={stat.value}>
            <span className="tm-trust__number">0{index + 1}</span>
            <div>
              <strong>{stat.value}</strong>
              <p>{stat.label}</p>
            </div>
          </div>
        ))}
      </section>

      <section className="tm-section tm-services" aria-labelledby="home-services">
        <div className="tm-section__top">
          <div>
            <p className="tm-eyebrow">01 / Our expertise</p>
            <h2 id="home-services">Where craft meets <em>capability.</em></h2>
          </div>
          <div className="tm-section__aside">
            <p>
              Functional, expressive and made for the spaces people use every day.
              Our work ranges from a single custom detail to larger fabrication needs.
            </p>
            <Link className="tm-inline-link" to="/services">
              All our services <span aria-hidden="true">↗</span>
            </Link>
          </div>
        </div>
        <div className="tm-services__grid">
          {servicePreviews.map((service, index) => (
            <Link to="/services" className="tm-service" key={service.title}>
              <div className="tm-service__image">
                <img src={service.image} alt={service.alt} loading="lazy" decoding="async" />
                <span className="tm-service__index">0{index + 1}</span>
              </div>
              <div className="tm-service__content">
                <div>
                  <h3>{service.title}</h3>
                  <p>{service.detail}</p>
                </div>
                <span className="tm-service__arrow" aria-hidden="true">↗</span>
              </div>
            </Link>
          ))}
        </div>
        <p className="tm-services__extra">
          Also offering {serviceGroups.slice(3).map((service) => service.title.toLowerCase()).join(" · ")}.
        </p>
      </section>

      <section className="tm-feature" aria-labelledby="home-feature-heading">
        <div className="tm-feature__visual">
          <img
            src={galleryItems[11].image}
            alt="Ornamental metalwork showing a decorative cut-out design"
            loading="lazy"
            decoding="async"
          />
        </div>
        <div className="tm-feature__copy">
          <p className="tm-eyebrow">Made for the details</p>
          <h2 id="home-feature-heading">The difference is in <em>the finish.</em></h2>
          <p>
            Beautiful metalwork should do more than look good in a photograph.
            It should suit its surroundings, serve its purpose and stand up to
            everyday use.
          </p>
          <div className="tm-feature__values">
            {promisePoints.slice(0, 2).map((point) => (
              <div key={point.title}>
                <span aria-hidden="true">✦</span>
                <div>
                  <h3>{point.title}</h3>
                  <p>{point.text}</p>
                </div>
              </div>
            ))}
          </div>
          <Link className="tm-inline-link" to="/about">
            Discover Touchmark <span aria-hidden="true">↗</span>
          </Link>
        </div>
      </section>

      <section className="tm-section tm-projects" aria-labelledby="home-projects">
        <div className="tm-section__top">
          <div>
            <p className="tm-eyebrow">02 / Selected portfolio</p>
            <h2 id="home-projects">A closer look at <em>our work.</em></h2>
          </div>
          <div className="tm-section__aside">
            <p>
              Explore the shapes, textures and techniques behind a selection
              of metalwork photographs from the Touchmark portfolio.
            </p>
            <Link className="tm-inline-link" to="/gallery">
              View photo gallery <span aria-hidden="true">↗</span>
            </Link>
          </div>
        </div>
        <div className="tm-projects__grid">
          {[mainProject, secondProject, thirdProject].map((project, index) => (
            <Link className="tm-project" key={project.title} to="/projects">
              <div className="tm-project__image">
                <img src={project.image} alt={project.title} loading="lazy" decoding="async" />
                <span>0{index + 1} / 03</span>
              </div>
              <div className="tm-project__content">
                <p>{project.location}</p>
                <h3>{project.title}</h3>
                <span aria-hidden="true">↗</span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="tm-contact" aria-labelledby="home-contact-heading">
        <div className="tm-contact__details">
          <p className="tm-eyebrow">03 / Start your project</p>
          <h2 id="home-contact-heading">Have something in <em>mind?</em></h2>
          <p>
            A gate, a railing, an architectural feature or a larger fabrication
            project — tell us what you are planning.
          </p>
          <div className="tm-contact__actions">
            <Link className="tm-action tm-action--light" to="/contact">
              Get in touch <span aria-hidden="true">↗</span>
            </Link>
            <a className="tm-action tm-action--outline" href={companyInfo.whatsappLink} target="_blank" rel="noopener noreferrer">
              WhatsApp us <span aria-hidden="true">↗</span>
            </a>
          </div>
        </div>
        <div className="tm-contact__decor" aria-hidden="true">
          <span>TOUCHMARK</span>
          <span>BUILT TO LAST</span>
        </div>
      </section>
    </div>
  );
}

export default Home;
